import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import {
    FaUserPlus, FaEdit, FaKey, FaTimes, FaUserCircle, FaPhone, FaUsers,
} from "react-icons/fa";
import { HiUser, HiLockClosed, HiPhone, HiMail, HiBriefcase } from "react-icons/hi";
import InputField from "../../components/inputFields/InputField";
import SearchableSelect from "../../components/inputFields/SearchableSelect";
import SearchableSelectField from "../../components/inputFields/SearchableSelectField";
import PageHeader from "../../components/header/PageHeader";
import {
    getFPOStaff, addFPOStaff, updateFPOStaff, setFPOStaffPassword,
} from "../../services/fpoStaff";

// ── Role options (must match backend FPOStaff.ROLE_CHOICES) ────────
const ROLE_OPTIONS = [
    { value: "fpo_admin",  label: "FPO Admin" },
    { value: "manager",    label: "Manager" },
    { value: "data_entry", label: "Data Entry" },
    { value: "accountant", label: "Accountant" },
    { value: "field_staff", label: "Field Staff" },
];

const roleLabel = (value) => ROLE_OPTIONS.find((r) => r.value === value)?.label || value;

const roleBadgeColor = (role) => ({
    fpo_admin:   "bg-purple-100 text-purple-700",
    manager:     "bg-blue-100 text-blue-700",
    data_entry:  "bg-amber-100 text-amber-700",
    accountant:  "bg-teal-100 text-teal-700",
    field_staff: "bg-green-100 text-green-700",
}[role] || "bg-gray-100 text-gray-600");

// ── Auto-generated username / temp password ─────────────────────
const generateUsername = (firstName, lastName) => {
    const base = `${firstName || ""}${lastName ? `.${lastName}` : ""}`
        .toLowerCase()
        .replace(/[^a-z0-9.]/g, "");
    const suffix = Math.floor(1000 + Math.random() * 9000);
    return `${base}${suffix}`;
};

const generateTempPassword = (length = 12) => {
    const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
    const lower = "abcdefghijkmnopqrstuvwxyz";
    const digits = "23456789";
    const symbols = "!@#$%&*";
    const all = upper + lower + digits + symbols;
    const pick = (set) => set[Math.floor(Math.random() * set.length)];

    const chars = [pick(upper), pick(lower), pick(digits), pick(symbols)];
    for (let i = chars.length; i < length; i += 1) chars.push(pick(all));
    for (let i = chars.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [chars[i], chars[j]] = [chars[j], chars[i]];
    }
    return chars.join("");
};

// ── Validation schemas ───────────────────────────────────────────
const AddStaffSchema = Yup.object().shape({
    first_name: Yup.string().required("First name is required"),
    last_name: Yup.string(),
    mobile_number: Yup.string().matches(/^[0-9]{10}$/, "Enter a valid 10-digit number"),
    email: Yup.string().email("Invalid email"),
    role: Yup.string().required("Role is required"),
    designation: Yup.string(),
});

const EditStaffSchema = Yup.object().shape({
    role: Yup.string().required("Role is required"),
    designation: Yup.string(),
});

const PasswordSchema = Yup.object().shape({
    new_password: Yup.string().min(8, "At least 8 characters").required("Password is required"),
    confirm_password: Yup.string()
        .oneOf([Yup.ref("new_password")], "Passwords do not match")
        .required("Please confirm the password"),
});

// ── Modal shell ──────────────────────────────────────────────────
const Modal = ({ title, icon: Icon, onClose, children }) => (
    <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[1100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
    >
        <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.97 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
        >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl">
                <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Icon className="text-primary text-sm" />
                    </div>
                    <h3 className="font-bold text-gray-800 text-sm">{title}</h3>
                </div>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
                    <FaTimes />
                </button>
            </div>
            <div className="p-5">{children}</div>
        </motion.div>
    </motion.div>
);

// ── Pagination ───────────────────────────────────────────────────
const PAGE_SIZE = 20;

const Pagination = ({ page, total, pageSize, onPage }) => {
    const pages = Math.ceil(total / pageSize) || 1;
    if (pages <= 1) return null;
    return (
        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
            <p className="text-xs text-gray-400">
                Showing {Math.min((page - 1) * pageSize + 1, total)}–{Math.min(page * pageSize, total)} of {total}
            </p>
            <div className="flex items-center gap-2">
                <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => onPage(page - 1)}
                    className="px-3 py-1.5 text-xs font-semibold border border-gray-200 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                >
                    Previous
                </button>
                <span className="text-xs text-gray-600 px-2">{page} / {pages}</span>
                <button
                    type="button"
                    disabled={page >= pages}
                    onClick={() => onPage(page + 1)}
                    className="px-3 py-1.5 text-xs font-semibold border border-gray-200 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                >
                    Next
                </button>
            </div>
        </div>
    );
};

// ── Main Component ───────────────────────────────────────────────
const FPOStaff = () => {
    const [staffList, setStaffList] = useState([]);
    const [total, setTotal]         = useState(0);
    const [page, setPage]           = useState(1);
    const [loading, setLoading]     = useState(true);
    const [error, setError]         = useState(null);

    const [showAddModal, setShowAddModal]         = useState(false);
    const [editingStaff, setEditingStaff]         = useState(null);
    const [passwordStaff, setPasswordStaff]       = useState(null);

    // Show/hide password toggles for the Set Password modal
    const [showNewPwd,     setShowNewPwd]     = useState(false);
    const [showConfirmPwd, setShowConfirmPwd] = useState(false);

    const openPasswordModal = (staff) => {
        setShowNewPwd(false);
        setShowConfirmPwd(false);
        setPasswordStaff(staff);
    };
    const closePasswordModal = () => {
        setShowNewPwd(false);
        setShowConfirmPwd(false);
        setPasswordStaff(null);
    };

    const [searchTerm, setSearchTerm]   = useState("");
    const [roleFilter, setRoleFilter]   = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");

    // debounce search so we don't fire a request on every keystroke
    const [debouncedSearch, setDebouncedSearch] = useState("");
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 350);
        return () => clearTimeout(t);
    }, [searchTerm]);
    useEffect(() => { setPage(1); }, [debouncedSearch, roleFilter, statusFilter]);

    // role/status still filtered client-side as a safety net over whatever the API returned
    const filteredStaff = useMemo(() => {
        return staffList.filter((staff) => {
            const matchesRole = roleFilter === "all" || staff.role === roleFilter;
            const matchesStatus = statusFilter === "all"
                || (statusFilter === "active" ? staff.is_active : !staff.is_active);
            return matchesRole && matchesStatus;
        });
    }, [staffList, roleFilter, statusFilter]);

    const loadStaff = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = { page, page_size: PAGE_SIZE };
            if (debouncedSearch) params.search = debouncedSearch;
            if (roleFilter !== "all") params.role = roleFilter;
            if (statusFilter !== "all") params.is_active = statusFilter === "active";
            const res  = await getFPOStaff(params);
            const data = res?.data;
            if (data?.status === 200 || data?.status === "200") {
                const results = data.results || data.data || [];
                setStaffList(results);
                setTotal(data.count ?? results.length);
            } else {
                setError(data?.message || "Failed to load staff.");
            }
        } catch (e) {
            setError("Unable to connect. Please check the server.");
        } finally {
            setLoading(false);
        }
    }, [page, debouncedSearch, roleFilter, statusFilter]);

    useEffect(() => { loadStaff(); }, [loadStaff]);

    // ── Add staff ────────────────────────────────────────────────
    const handleAddStaff = async (values, { setSubmitting, resetForm }) => {
        try {
            const payload = {
                ...values,
                username: generateUsername(values.first_name, values.last_name),
                password: generateTempPassword(),
            };
            const res  = await addFPOStaff(payload);
            const data = res?.data;
            if (data?.status === 201 || data?.status === "201") {
                toast.success("Staff member added! Now set their password. ✅");
                resetForm();
                setShowAddModal(false);
                await loadStaff();
                if (data.data) openPasswordModal(data.data);
            } else {
                toast.error(data?.message || "Failed to add staff.");
            }
        } catch (e) {
            const errors = e?.response?.data?.errors;
            const firstError = errors && Object.values(errors)[0]?.[0];
            toast.error(firstError || e?.response?.data?.message || "Something went wrong.");
        }
        setSubmitting(false);
    };

    // ── Edit staff ───────────────────────────────────────────────
    const handleEditStaff = async (values, { setSubmitting }) => {
        try {
            const res  = await updateFPOStaff(editingStaff.id, values);
            const data = res?.data;
            if (data?.status === 200 || data?.status === "200") {
                toast.success("Staff member updated successfully! ✅");
                setEditingStaff(null);
                await loadStaff();
            } else {
                toast.error(data?.message || "Update failed.");
            }
        } catch (e) {
            toast.error(e?.response?.data?.message || "Something went wrong.");
        }
        setSubmitting(false);
    };

    // ── Toggle active status ────────────────────────────────────
    const [togglingId, setTogglingId] = useState(null);

    const handleToggleStatus = async (staff) => {
        setTogglingId(staff.id);
        const nextActive = !staff.is_active;
        setStaffList((prev) => prev.map((s) => (s.id === staff.id ? { ...s, is_active: nextActive } : s)));
        try {
            const res  = await updateFPOStaff(staff.id, { is_active: nextActive });
            const data = res?.data;
            if (data?.status === 200 || data?.status === "200") {
                toast.success(`Staff member ${nextActive ? "activated" : "deactivated"}. ✅`);
            } else {
                setStaffList((prev) => prev.map((s) => (s.id === staff.id ? { ...s, is_active: staff.is_active } : s)));
                toast.error(data?.message || "Failed to update status.");
            }
        } catch (e) {
            setStaffList((prev) => prev.map((s) => (s.id === staff.id ? { ...s, is_active: staff.is_active } : s)));
            toast.error(e?.response?.data?.message || "Something went wrong.");
        }
        setTogglingId(null);
    };

    // ── Set password ─────────────────────────────────────────────
    const handleSetPassword = async (values, { setSubmitting, resetForm }) => {
        try {
            const res  = await setFPOStaffPassword(passwordStaff.id, values.new_password);
            const data = res?.data;
            if (data?.status === 200 || data?.status === "200") {
                toast.success("Password updated successfully! 🔑");
                resetForm();
                setPasswordStaff(null);
            } else {
                toast.error(data?.message || "Failed to update password.");
            }
        } catch (e) {
            const errors = e?.response?.data?.errors;
            const firstError = errors && Object.values(errors)[0]?.[0];
            toast.error(firstError || e?.response?.data?.message || "Something went wrong.");
        }
        setSubmitting(false);
    };

    return (
        <div className="w-full pb-12">
            {/* ── Page header ── */}
            <PageHeader
                title="FPO Staff"
                search={searchTerm}
                onSearchChange={setSearchTerm}
                searchPlaceholder="Search by name, username, mobile or designation..."
                actionLabel="Add Staff"
                actionIcon={FaUserPlus}
                onAction={() => setShowAddModal(true)}
            />

            {loading ? (
                <div className="flex items-center justify-center h-64">
                    <div className="flex flex-col items-center gap-3">
                        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                        <p className="text-gray-500 text-sm">Loading staff...</p>
                    </div>
                </div>
            ) : error ? (
                <div className="flex flex-col items-center justify-center h-64 gap-3">
                    <p className="text-red-500 font-medium">{error}</p>
                    <button onClick={loadStaff} className="px-4 py-2 bg-primary text-white rounded-lg text-sm">
                        Retry
                    </button>
                </div>
            ) : (
            <>
            {/* ── Filter toolbar ──────────────────────────────── */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-4 flex items-center gap-3 flex-wrap">
                <div className="w-44">
                    <SearchableSelect
                        value={roleFilter}
                        onChange={setRoleFilter}
                        options={[{ value: "all", label: "All Roles" }, ...ROLE_OPTIONS]}
                        className="pl-4 pr-8 py-2.5 text-sm border border-gray-200 rounded-xl bg-white text-gray-800"
                    />
                </div>

                <div className="w-40">
                    <SearchableSelect
                        value={statusFilter}
                        onChange={setStatusFilter}
                        options={[
                            { value: "all", label: "All Status" },
                            { value: "active", label: "Active" },
                            { value: "inactive", label: "Inactive" },
                        ]}
                        className="pl-4 pr-8 py-2.5 text-sm border border-gray-200 rounded-xl bg-white text-gray-800"
                    />
                </div>
            </div>

            {/* ── Staff table ──────────────────────────────────── */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                {filteredStaff.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 gap-2">
                        <FaUsers className="text-gray-200 text-4xl" />
                        <p className="text-gray-400 text-sm">
                            {staffList.length === 0 && !searchTerm && roleFilter === "all" && statusFilter === "all"
                                ? "No staff members yet."
                                : "No staff members match your search/filters."}
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-100 bg-gray-50/60">
                                    <th className="text-left font-semibold text-gray-500 text-xs uppercase tracking-wide px-5 py-3">Staff</th>
                                    <th className="text-left font-semibold text-gray-500 text-xs uppercase tracking-wide px-5 py-3">Contact</th>
                                    <th className="text-left font-semibold text-gray-500 text-xs uppercase tracking-wide px-5 py-3">Role</th>
                                    <th className="text-left font-semibold text-gray-500 text-xs uppercase tracking-wide px-5 py-3">Designation</th>
                                    <th className="text-left font-semibold text-gray-500 text-xs uppercase tracking-wide px-5 py-3">Status</th>
                                    <th className="text-right font-semibold text-gray-500 text-xs uppercase tracking-wide px-5 py-3">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {filteredStaff.map((staff, i) => (
                                    <motion.tr
                                        key={staff.id}
                                        initial={{ opacity: 0, y: 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: i * 0.03 }}
                                        className="hover:bg-gray-50/60 transition-colors"
                                    >
                                        <td className="px-5 py-3.5">
                                            <div className="flex items-center gap-3 min-w-0">
                                                {staff.user?.photo ? (
                                                    <img
                                                        src={staff.user.photo}
                                                        alt={staff.user.name}
                                                        className="w-10 h-10 rounded-full object-cover border border-gray-200 flex-shrink-0"
                                                    />
                                                ) : (
                                                    <div className="w-10 h-10 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
                                                        <FaUserCircle className="text-primary text-xl" />
                                                    </div>
                                                )}
                                                <div className="min-w-0">
                                                    <p className="font-bold text-gray-800 truncate">{staff.user?.name}</p>
                                                    {staff.username && (
                                                        <p className="text-xs text-gray-400 truncate">@{staff.username}</p>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-5 py-3.5">
                                            <div className="flex items-center gap-1.5 text-xs text-gray-500">
                                                <FaPhone className="text-[10px] text-gray-300" />
                                                <span>{staff.user?.mobile_number || "Not provided"}</span>
                                            </div>
                                        </td>
                                        <td className="px-5 py-3.5">
                                            <span className={`text-[10px] font-semibold px-2.5 py-1 rounded-full uppercase tracking-wide ${roleBadgeColor(staff.role)}`}>
                                                {staff.role_display || roleLabel(staff.role)}
                                            </span>
                                        </td>
                                        <td className="px-5 py-3.5 text-gray-500">{staff.designation || "—"}</td>
                                        <td className="px-5 py-3.5">
                                            <button
                                                type="button"
                                                title={staff.is_active ? "Click to deactivate" : "Click to activate"}
                                                onClick={() => handleToggleStatus(staff)}
                                                disabled={togglingId === staff.id}
                                                className="inline-flex items-center gap-2 disabled:opacity-60 cursor-pointer"
                                            >
                                                <span className={`relative w-10 h-[22px] rounded-full transition-colors ${staff.is_active ? "bg-primary" : "bg-gray-200"}`}>
                                                    <span className={`absolute top-[2px] left-[2px] bg-white rounded-full h-[18px] w-[18px] shadow-sm transition-transform ${staff.is_active ? "translate-x-[18px]" : ""}`} />
                                                </span>
                                                <span className={`text-[10px] font-semibold uppercase tracking-wide ${staff.is_active ? "text-green-700" : "text-gray-500"}`}>
                                                    {staff.is_active ? "Active" : "Inactive"}
                                                </span>
                                            </button>
                                        </td>
                                        <td className="px-5 py-3.5">
                                            <div className="flex items-center justify-end gap-1">
                                                <button
                                                    type="button"
                                                    title="Edit"
                                                    onClick={() => setEditingStaff(staff)}
                                                    className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-primary hover:bg-primary/10 transition-colors"
                                                >
                                                    <FaEdit />
                                                </button>
                                                <button
                                                    type="button"
                                                    title="Set Password"
                                                    onClick={() => openPasswordModal(staff)}
                                                    className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-primary hover:bg-primary/10 transition-colors"
                                                >
                                                    <FaKey />
                                                </button>
                                            </div>
                                        </td>
                                    </motion.tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                <Pagination page={page} total={total} pageSize={PAGE_SIZE} onPage={setPage} />
            </div>
            </>
            )}

            {/* ── Add Staff Modal ──────────────────────────────── */}
            <AnimatePresence>
                {showAddModal && (
                    <Modal title="Add Staff Member" icon={FaUserPlus} onClose={() => setShowAddModal(false)}>
                        <Formik
                            initialValues={{
                                first_name: "", last_name: "",
                                mobile_number: "", email: "", role: "data_entry", designation: "",
                            }}
                            validationSchema={AddStaffSchema}
                            onSubmit={handleAddStaff}
                        >
                            {({ isSubmitting }) => (
                                <Form className="flex flex-col gap-4">
                                    <p className="text-xs text-gray-400 -mb-1">
                                        A login username will be generated automatically. You'll be prompted to set a password right after.
                                    </p>
                                    <div className="grid grid-cols-2 gap-3">
                                        <InputField name="first_name" label="First Name" required leftIcon={HiUser} placeholder="First name" />
                                        <InputField name="last_name" label="Last Name" leftIcon={HiUser} placeholder="Last name" />
                                    </div>
                                    <InputField name="mobile_number" label="Mobile Number" leftIcon={HiPhone} placeholder="10-digit mobile number" maxLength={10} />
                                    <InputField name="email" label="Email" leftIcon={HiMail} type="email" placeholder="Email address" />

                                    <SearchableSelectField name="role" label="Role" required options={ROLE_OPTIONS} />

                                    <InputField name="designation" label="Designation" leftIcon={HiBriefcase} placeholder="e.g. Field Officer" />

                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="w-full mt-1 py-3 px-6 bg-primary hover:bg-green-600 text-white font-semibold rounded-xl shadow-lg shadow-primary/20 transition-colors disabled:opacity-60"
                                    >
                                        {isSubmitting ? "Adding..." : "Add Staff Member"}
                                    </button>
                                </Form>
                            )}
                        </Formik>
                    </Modal>
                )}
            </AnimatePresence>

            {/* ── Edit Staff Modal ─────────────────────────────── */}
            <AnimatePresence>
                {editingStaff && (
                    <Modal title={`Edit ${editingStaff.user?.name}`} icon={FaEdit} onClose={() => setEditingStaff(null)}>
                        <Formik
                            initialValues={{
                                role: editingStaff.role,
                                designation: editingStaff.designation || "",
                                is_active: editingStaff.is_active,
                            }}
                            validationSchema={EditStaffSchema}
                            onSubmit={handleEditStaff}
                        >
                            {({ isSubmitting, values, setFieldValue }) => (
                                <Form className="flex flex-col gap-4">
                                    <SearchableSelectField name="role" label="Role" required options={ROLE_OPTIONS} />

                                    <InputField name="designation" label="Designation" leftIcon={HiBriefcase} placeholder="e.g. Field Officer" />

                                    <label className="inline-flex items-center gap-3 cursor-pointer mt-1">
                                        <input
                                            type="checkbox"
                                            className="sr-only peer"
                                            checked={values.is_active}
                                            onChange={(e) => setFieldValue("is_active", e.target.checked)}
                                        />
                                        <div className={`relative w-11 h-6 rounded-full transition-colors ${values.is_active ? "bg-primary" : "bg-gray-200"}`}>
                                            <div className={`absolute top-[2px] left-[2px] bg-white border border-gray-300 rounded-full h-5 w-5 transition-transform ${values.is_active ? "translate-x-full border-white" : ""}`} />
                                        </div>
                                        <span className="text-sm font-medium text-gray-700">Active</span>
                                    </label>

                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="w-full mt-1 py-3 px-6 bg-primary hover:bg-green-600 text-white font-semibold rounded-xl shadow-lg shadow-primary/20 transition-colors disabled:opacity-60"
                                    >
                                        {isSubmitting ? "Saving..." : "Save Changes"}
                                    </button>
                                </Form>
                            )}
                        </Formik>
                    </Modal>
                )}
            </AnimatePresence>

            {/* ── Set Password Modal ───────────────────────────── */}
            <AnimatePresence>
                {passwordStaff && (
                    <Modal title={`Set Password – ${passwordStaff.user?.name}`} icon={FaKey} onClose={closePasswordModal}>
                        <Formik
                            initialValues={{ new_password: "", confirm_password: "" }}
                            validationSchema={PasswordSchema}
                            onSubmit={handleSetPassword}
                        >
                            {({ isSubmitting }) => (
                                <Form className="flex flex-col gap-4">
                                    <InputField
                                        name="new_password"
                                        label="New Password"
                                        required
                                        leftIcon={HiLockClosed}
                                        type="password"
                                        placeholder="Minimum 8 characters"
                                        isPassword
                                        showPassword={showNewPwd}
                                        onPasswordToggle={() => setShowNewPwd(p => !p)}
                                    />
                                    <InputField
                                        name="confirm_password"
                                        label="Confirm Password"
                                        required
                                        leftIcon={HiLockClosed}
                                        type="password"
                                        placeholder="Re-enter password"
                                        isPassword
                                        showPassword={showConfirmPwd}
                                        onPasswordToggle={() => setShowConfirmPwd(p => !p)}
                                    />
                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="w-full mt-1 py-3 px-6 bg-primary hover:bg-green-600 text-white font-semibold rounded-xl shadow-lg shadow-primary/20 transition-colors disabled:opacity-60"
                                    >
                                        {isSubmitting ? "Updating..." : "Update Password"}
                                    </button>
                                </Form>
                            )}
                        </Formik>
                    </Modal>
                )}
            </AnimatePresence>
        </div>
    );
};

export default FPOStaff;
